import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ContestTask, ContestTaskDto, ContestTaskStats, ContestTaskType} from '../model/contest/ContestTask';
import {environment} from '../../environments/environment';
import {map} from 'rxjs/operators';
import {Locale} from '../model/Locale';
import {SchoolGrade} from '../model/location/School';
import {
  ContestEvent,
  ContestEventData,
  ContestEventDto,
  ContestEventType,
  ContestProgress,
  ContestProgressDto
} from '../model/contest/ContestEvent';
import {Role} from '../model/auth/Role';

@Injectable({
  providedIn: 'root'
})
export class ContestTaskService {

  constructor(private http: HttpClient) {
  }

  private static COMPETITION_ID = 'COMPETITION_ID';
  private static CONTEST_ID = 'CONTEST_ID';
  private static TASK_ID = 'TASK_ID';
  private static TASK_GRADE = 'TASK_GRADE';
  private static TASK_TYPE = 'TASK_TYPE';
  private static TASK_LOCALE = 'TASK_LOCALE';
  private static EVENT_TYPE = 'EVENT_TYPE';
  private static USER_ID = 'USER_ID';
  private static ROLE = 'ROLE';

  private static CONTESTS_BASE_URL = '/contests';
  private static CONTESTS_DETAILS_URL = `${ContestTaskService.CONTESTS_BASE_URL}/${ContestTaskService.CONTEST_ID}`;
  private static CONTESTS_PROGRESS_URL = `${ContestTaskService.CONTESTS_DETAILS_URL}/progress`;
  private static CONTESTS_CLEAR_PROGRESS_URL = `/competitions/${ContestTaskService.COMPETITION_ID}${ContestTaskService.CONTESTS_DETAILS_URL}/roles/${ContestTaskService.ROLE}/users/${ContestTaskService.USER_ID}/progress`;

  private static CONTESTS_TASKS_BASE_URL = `tasks/${ContestTaskService.TASK_TYPE}/${ContestTaskService.TASK_ID}`;
  private static CONTESTS_TASKS_DETAILS = `${ContestTaskService.CONTESTS_DETAILS_URL}/${ContestTaskService.CONTESTS_TASKS_BASE_URL}`;
  private static CONTESTS_TASKS_STATS = `${ContestTaskService.CONTESTS_TASKS_DETAILS}/stats`;

  private static CONTESTS_TASKS_URL = `${ContestTaskService.CONTESTS_DETAILS_URL}/grades/${ContestTaskService.TASK_GRADE}/tasks/${ContestTaskService.TASK_TYPE}`;
  private static CONTESTS_TASKS_LOCALE_URL = `${ContestTaskService.CONTESTS_TASKS_URL}/locales/${ContestTaskService.TASK_LOCALE}`;

  private static CONTESTS_EVENTS_URL = `/${ContestTaskService.CONTESTS_TASKS_BASE_URL}/events/${ContestTaskService.EVENT_TYPE}`;

  saveContestTask(contestId: string, data: ContestTask, grade: SchoolGrade, type: ContestTaskType): Observable<ContestTask> {
    const url = ContestTaskService.CONTESTS_TASKS_URL
      .replace(ContestTaskService.CONTEST_ID, contestId)
      .replace(ContestTaskService.TASK_GRADE, grade)
      .replace(ContestTaskService.TASK_TYPE, type);

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => ContestTask.parse(data as ContestTaskDto))
      );
  }

  getContestTask(contestId: string, taskId: string, type: ContestTaskType): Observable<ContestTask> {
    const url = ContestTaskService.CONTESTS_TASKS_DETAILS
      .replace(ContestTaskService.CONTEST_ID, contestId)
      .replace(ContestTaskService.TASK_ID, taskId)
      .replace(ContestTaskService.TASK_TYPE, type);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => ContestTask.parse(data as ContestTaskDto))
      );
  }

  getContestTaskStats(contestId: string, taskId: string, type: ContestTaskType): Observable<ContestTaskStats> {
    const url = ContestTaskService.CONTESTS_TASKS_STATS
      .replace(ContestTaskService.CONTEST_ID, contestId)
      .replace(ContestTaskService.TASK_ID, taskId)
      .replace(ContestTaskService.TASK_TYPE, type);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => data as ContestTaskStats)
      );
  }

  getContestProgress(contestId: string, userId?: string): Observable<ContestProgress> {
    const url = ContestTaskService.CONTESTS_PROGRESS_URL
      .replace(ContestTaskService.CONTEST_ID, contestId) + (userId ? `?user_id=${userId}` : '');

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => ContestProgress.parse(data as ContestProgressDto))
      );
  }

  clearContestProgress(competitionId: string, contestId: string, userId: string, role = Role.Student): Observable<unknown> {
    const url = ContestTaskService.CONTESTS_CLEAR_PROGRESS_URL
      .replace(ContestTaskService.COMPETITION_ID, competitionId)
      .replace(ContestTaskService.CONTEST_ID, contestId)
      .replace(ContestTaskService.ROLE, role)
      .replace(ContestTaskService.USER_ID, userId);

    return this.http.delete(`${environment.api.host}${url}`);
  }

  deleteContestTaskLocale(contestId: string, grade: SchoolGrade, type: ContestTaskType, locale: Locale): Observable<any> {
    const url = ContestTaskService.CONTESTS_TASKS_LOCALE_URL
      .replace(ContestTaskService.CONTEST_ID, contestId)
      .replace(ContestTaskService.TASK_GRADE, grade)
      .replace(ContestTaskService.TASK_TYPE, type)
      .replace(ContestTaskService.TASK_LOCALE, locale);

    return this.http.delete(`${environment.api.host}${url}`);
  }

  saveContestEvent(taskId: string, taskType: ContestTaskType, eventType: ContestEventType,
                   data?: ContestEventData): Observable<ContestEvent> {
    const url = ContestTaskService.CONTESTS_EVENTS_URL
      .replace(ContestTaskService.TASK_TYPE, taskType)
      .replace(ContestTaskService.TASK_ID, taskId)
      .replace(ContestTaskService.EVENT_TYPE, eventType);


    return this.http.post(`${environment.api.host}${url}`, data || {})
      .pipe(
        map(data => ContestEvent.parse(data as ContestEventDto))
      );
  }
}
